import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { Box, Button } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';

import { CissTextField } from 'components/fields';
import { ContentModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { ListagemProvider } from 'context/ListagemContext';
import { PermissoesUsuariosModalGrid } from 'pages/cadastro/permissoes/components';
import { PermissoesProps, UsuariosProps } from 'pages/cadastro/permissoes/types';

type PermissoesUsuariosModal = {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
};

export function PermissoesUsuariosModal({ open, setOpen }: PermissoesUsuariosModal) {
    const { values, setFieldValue } = useFormikContext<PermissoesProps>();

    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
    const [query, setQuery] = useState('');
    const usuariosCacheRef = useRef<Map<number, UsuariosProps>>(new Map());

    /**
     * Ao clicar para adicionar, mescla os novos usuários com os já existentes e fecha o modal.
     */
    const handleConfirmModal = useCallback(() => {
        const existingUsuarios = values.usuarios || [];
        const existingIds = new Set(existingUsuarios.map((u) => u.idUsuario));

        const novosUsuarios = Array.from(selectionModel.ids)
            .map((id) => usuariosCacheRef.current.get(id as number))
            .filter((u): u is UsuariosProps => !!u && !existingIds.has(u.idUsuario));

        setFieldValue('usuarios', [...existingUsuarios, ...novosUsuarios]);

        setOpen(false);
    }, [selectionModel, values.usuarios, setFieldValue, setOpen]);

    /**
     * Reinicia o estado do modal ao abrir.
     */
    useEffect(() => {
        if (open) {
            usuariosCacheRef.current = new Map();
            setSelectionModel({ type: 'include', ids: new Set() });
            setQuery('');
        }
    }, [open]);

    return (
        <ContentModal
            open={open}
            setOpen={setOpen}
            closeButton
            centered={false}
            sx={{
                maxWidth: 960,
                width: '90vw',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
            }}
        >
            <SubTitle label="Adicionar usuários" />

            <CissTextField
                size="small"
                label="Pesquisar"
                placeholder="Digite um usuário..."
                deboundTime={500}
                onChange={(e) => setQuery(e.target.value)}
                sx={{
                    mb: 2,
                    width: { xs: '100%', md: '50%' },
                }}
            />

            <ListagemProvider>
                <PermissoesUsuariosModalGrid
                    query={query}
                    selectionModel={selectionModel}
                    setSelectionModel={setSelectionModel}
                    usuariosCacheRef={usuariosCacheRef}
                />
            </ListagemProvider>

            <Box
                sx={{
                    mt: 2,
                    gap: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <Button variant="outlined" onClick={() => setOpen(false)}>
                    Cancelar
                </Button>

                <Button variant="contained" onClick={handleConfirmModal}>
                    Adicionar
                </Button>
            </Box>
        </ContentModal>
    );
}
